import { isColorDark } from "@/lib/color";

import type { PinAnnotation } from "@/types/annotation";

/**
 * Geometry shared by every surface that draws a pin — the live Konva editor,
 * the SVG thumbnail overlay, the flattened download and the patient export.
 * They must agree or a chart looks different once it leaves the editor.
 */

// How far above the site the badge sits when a pin is first placed, in
// relative units. Relative coordinates are not square, so on a very wide or
// very tall image the stem reads a little shorter or longer; the provider can
// drag the badge anywhere afterwards.
const DEFAULT_LABEL_DY = -0.06;

const MIN_PIN_SCALE = 0.45;

/** Badge placement for a freshly dropped pin: straight above the site. */
export const defaultPinLabelPosition = (position: {
  x: number;
  y: number;
}): { x: number; y: number } => ({
  x: position.x,
  y: Math.max(position.y + DEFAULT_LABEL_DY, 0.02),
});

/**
 * Pin scale for an image whose smaller side is `minDim` px.
 *
 * Every surface that draws a pin needs the badge to look the same size
 * relative to the anatomy, whether it is a 300px thumbnail or a full-
 * resolution export. Deriving it here — rather than letting each renderer
 * pick its own multiplier — is what keeps them in agreement. The divisor is
 * the editor's typical displayed height, so on-screen geometry comes out at
 * roughly 1.
 *
 * The floor stops the badge collapsing into an unreadable speck on gallery
 * thumbnails, which are only a couple of hundred pixels tall; below that size
 * a strictly proportional badge cannot show its number.
 */
export const pinScaleForImage = (minDim: number): number =>
  Math.max(minDim / 700, MIN_PIN_SCALE);

/** Radius of the dot marking the injection site, in px. */
export const pinAnchorRadius = (scale: number): number => 3 * scale;

/** Font size for the number inside the badge, in px. */
export const pinBadgeFontSize = (scale: number): number => 11 * scale;

export const pinBadgeCornerRadius = (scale: number): number => 3 * scale;

/**
 * Badge box for `text` at the given scale. Width grows with the digit count so
 * "12.5" doesn't overflow a box sized for "2".
 */
export const pinBadgeSize = (
  text: string,
  scale: number,
): { width: number; height: number } => {
  const fontSize = pinBadgeFontSize(scale);
  // Approximate advance width — avoids a canvas measureText round trip in the
  // SVG surface, and the padding absorbs the error.
  const textWidth = text.length * fontSize * 0.6;
  return {
    width: Math.max(textWidth + fontSize * 0.7, fontSize * 1.6),
    height: fontSize * 1.5,
  };
};

/** The label text a pin's badge shows. */
export const pinLabelText = (annotation: PinAnnotation): string =>
  String(annotation.amount);

/**
 * Where the leader line should stop so it meets the badge's edge instead of
 * running under it. Walks from the site toward the badge centre and clips at
 * the badge's bounding box.
 */
export const pinLeaderEnd = (
  site: { x: number; y: number },
  badgeCenter: { x: number; y: number },
  badge: { width: number; height: number },
): { x: number; y: number } => {
  const dx = badgeCenter.x - site.x;
  const dy = badgeCenter.y - site.y;
  if (dx === 0 && dy === 0) return badgeCenter;

  const halfWidth = badge.width / 2;
  const halfHeight = badge.height / 2;
  // Largest t in [0,1] where the ray is still outside the badge box.
  const tx = dx === 0 ? Infinity : halfWidth / Math.abs(dx);
  const ty = dy === 0 ? Infinity : halfHeight / Math.abs(dy);
  const t = Math.max(0, 1 - Math.min(tx, ty));

  return { x: site.x + dx * t, y: site.y + dy * t };
};

/**
 * Number colour for a badge filled with `color`. The palette runs from white
 * through to near-black, so the label has to flip to stay readable.
 */
export const pinBadgeTextColor = (color: string): string =>
  isColorDark(color) ? "#FFFFFF" : "#1E293B";

/** Traces a rounded rectangle into the current path. Caller fills or strokes. */
export const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
};
